import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useAuthStore } from "../../zustand";

/**
 * Custom hook for fetching and caching physiotherapist client history
 *
 * Provides list of clients/patients that the physiotherapist has interacted with.
 * Only enabled for physio_therapist users; returns empty for other user types.
 * Uses TanStack Query as single source of truth for client data caching.
 * Data is cached per language to support localized client information.
 *
 * Access Control:
 * - Only fetches when user is authenticated AND currentProduct === 'physio_therapist'
 * - Returns empty array when disabled
 *
 * Cache Strategy:
 * - Query key format: ['therapistHistory', email, preferredLanguage]
 * - Separate cache entries per therapist email and language
 * - staleTime: 5 minutes (client list changes moderately)
 * - gcTime: 10 minutes (keep in memory)
 * - Refetch on mount if stale, on network reconnect, but NOT on window focus
 *
 * API Endpoint:
 * - GET /api/physio/clients/{language}
 * - Returns: {clients: [...]} array of client objects with contact/interaction history
 *
 * @returns {Object} Query result object
 * @returns {Array} result.clients - Array of client/patient objects, empty if disabled
 * @returns {boolean} result.isLoading - True on first load (no cached data)
 * @returns {boolean} result.isFetching - True while any fetch is in progress
 * @returns {Error} result.error - Error object if fetch failed
 * @returns {Function} result.refetch - Manual refetch function
 */
export const useTherapistHistoryQuery = () => {
  // Get current user email from auth store (used as cache key and access control)
  const email = useAuthStore((state) => state.user.email);
  // Get user's language preference from auth store (used as cache key dimension)
  const preferredLanguage = useAuthStore((state) => state.preferredLanguage);
  // Get current product type from auth store (determines if query should run)
  const currentProduct = useAuthStore((state) => state.currentProduct);
  // Determine if user is a physiotherapist (only physio can access client history)
  const isPhysioTherapist = currentProduct === "physio_therapist";

  /**
   * TanStack Query hook for managing therapist client history fetching and caching
   * Query is disabled unless user is authenticated AND is a physio therapist
   */
  const query = useQuery({
    // Cache key dimensions: per therapist email and language for localized client data
    queryKey: ["therapistHistory", email, preferredLanguage],

    /**
     * Query function: fetch physiotherapist's client list from API
     * @async
     * @returns {Array} Array of client objects with contact/interaction history
     */
    queryFn: async () => {
      // Fetch client list from physio API endpoint with language parameter
      const response = await axios.get(
        `${process.env.REACT_APP_FIREBASE_API_HOST}/api/physio/clients/${preferredLanguage}`,
        {
          // Include credentials for authentication
          withCredentials: true,
        }
      );

      // Return clients array from response, or empty array if missing
      return response.data.clients || [];
    },

    // Only enable query when both conditions are met:
    // 1. User is authenticated (email exists)
    // 2. User is a physiotherapist (has physio_therapist product)
    enabled: !!email && isPhysioTherapist,

    // Data freshness: 5 minutes before marking as "stale" (client list changes moderately)
    staleTime: 5 * 60 * 1000,
    // Garbage collection: keep cached data in memory for 10 minutes after last usage
    gcTime: 10 * 60 * 1000,
    // Refetch if stale when component mounts (therapist returns to client screen)
    refetchOnMount: true,
    // Do NOT refetch when user switches browser tabs (reduces unnecessary API calls)
    refetchOnWindowFocus: false,
    // Refetch when network connectivity is restored (ensures fresh client list)
    refetchOnReconnect: true,
    // Keep previous language data while fetching new language data
    // Prevents UI flicker when switching languages
    keepPreviousData: true,

    // Retry failed requests up to 2 times with exponential backoff
    // Backoff: 1s, 2s (up to 10s max)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Return subset of query state useful for UI components
  // Note: returns 'clients' instead of 'data' for semantic clarity in physio context
  return {
    clients: query.data, // Array of client objects, empty if user is not physio
    isLoading: query.isLoading, // True during initial load (no cached data exists)
    isFetching: query.isFetching, // True while any fetch is in progress (including background refetch)
    error: query.error, // Error object if fetch failed, null if successful
    refetch: query.refetch, // Function to manually trigger refetch
  };
};
